import { Edit as EditIcon } from '@mui/icons-material';
import { Box } from '@mui/material';
import {
    DataGridPro,
    DataGridProProps,
    GridColDef,
    GridToolbar,
    useGridApiRef,
    GridActionsCellItem,
    GridValueFormatterParams,
    GridValueGetterParams,
} from '@mui/x-data-grid-pro';
import { parseISO } from 'date-fns';
import React, { useMemo } from 'react';

import { StatAlertsStatus } from 'app/enum/stat-alerts';
import { StatAlerts } from 'app/model/redshift/stat-alerts';
import { StatAlertDialog } from 'features/qa/stat-alert/stat-alert-dialog';

const StatAlertsStatusLabels = ['Open', 'In Process', 'Completed', 'Cancelled', 'Duplicate'];
export const StatAlertGrid: React.FunctionComponent<Omit<DataGridProProps<StatAlerts>, 'columns'>> = ({ ...rest }) => {
    const [open, setOpen] = React.useState(false);
    const [alertId, setalertId] = React.useState<number>(0);
    const [status, setStatus] = React.useState(StatAlertsStatus.OPEN);
    const apiRef = useGridApiRef();

    const handleClose = (id: number, curStatus: StatAlertsStatus, withConfirmation = true): void => {
        setOpen(false);
        if (withConfirmation) {
            apiRef.current.updateRows([{ id, status: curStatus }]);
        }
    };
    const handleStatusEdit = React.useCallback(
        (row: StatAlerts) => () => {
            // Change value of alertId/status for StatAlertDialog
            // to reflect the change

            setalertId(row.id);
            setStatus(row.status!);

            setOpen(true);
        },
        [],
    );

    const columns = useMemo<Array<GridColDef<StatAlerts>>>(() => {
        return [
            {
                field: 'id',
                headerName: 'Alert Id',
                type: 'string',
                flex: 1,
            },
            {
                field: 'brandId',
                headerName: 'Brand Id',
                type: 'string',
                flex: 1,
            },
            {
                field: 'brandName',
                headerName: 'Brand Name',
                type: 'string',
                flex: 1,
            },
            {
                field: 'locationVerified',
                headerName: 'Location Verified',
                type: 'singleSelect',
                valueOptions: rest.rows.map(({ locationVerified }) => locationVerified),
                flex: 1,
            },
            {
                field: 'midmatchVerified',
                headerName: 'Midmatch Verified',
                type: 'singleSelect',
                valueOptions: rest.rows.map(({ midmatchVerified }) => midmatchVerified),
                flex: 1,
            },
            {
                field: 'category',
                headerName: 'Category',
                type: 'string',
                flex: 1,
            },
            {
                field: 'createdDate',
                headerName: 'Created Date',
                type: 'date',
                valueFormatter: (params: GridValueFormatterParams<Date | undefined>) =>
                    params.value?.toLocaleDateString() ?? '',
                valueGetter: (params: GridValueGetterParams<StatAlerts, StatAlerts['createdDate']>) =>
                    params.value ? parseISO(params.value) : undefined,
                flex: 1,
            },
            {
                field: 'fileDate',
                headerName: 'File Date',
                type: 'date',
                valueFormatter: (params: GridValueFormatterParams<Date | undefined>) =>
                    params.value?.toLocaleDateString() ?? '',
                valueGetter: (params: GridValueGetterParams<StatAlerts, StatAlerts['fileDate']>) =>
                    params.value ? parseISO(params.value) : undefined,
                flex: 1,
            },
            {
                field: 'alertLevel',
                headerName: 'Alert Level',
                type: 'singleSelect',
                valueOptions: rest.rows.map(({ alertLevel }) => alertLevel),
                flex: 1,
            },
            {
                field: 'alertPeriod',
                headerName: 'Alert Period',
                type: 'singleSelect',
                valueOptions: rest.rows.map(({ alertPeriod }) => alertPeriod),
                flex: 1,
            },
            {
                field: 'previousSpend',
                headerName: 'Previous Spend',
                type: 'number',
                flex: 1,
            },
            {
                field: 'currentSpend',
                headerName: 'Current Spend',
                type: 'number',
                flex: 1,
            },
            {
                field: 'percentDiff',
                headerName: 'Percent Diff',
                type: 'number',
                flex: 1,
            },
            {
                field: 'priority',
                headerName: 'Priority',
                type: 'string',
                flex: 1,
            },
            {
                field: 'status',
                headerName: 'Status',
                // editable: true,
                type: 'string',
                flex: 1,
                valueFormatter: (params: GridValueFormatterParams<StatAlerts['status']>) =>
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    StatAlertsStatusLabels[params.value!],
            },
            {
                field: 'actions',
                headerName: 'Actions',
                type: 'actions',
                getActions: (params) => [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label='Edit'
                        onClick={handleStatusEdit(params.row)}
                        showInMenu={false}
                    />,
                ],
            },
        ];
    }, [handleStatusEdit, rest.rows]);

    return (
        <Box sx={{ height: '100%' }}>
            <Box sx={{ width: '100%', display: 'grid', height: '100%' }}>
                <DataGridPro apiRef={apiRef} components={{ Toolbar: GridToolbar }} {...rest} columns={columns} />
            </Box>
            <StatAlertDialog open={open} alertId={alertId} status={status} onClose={handleClose} />
        </Box>
    );
};
